import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useFirebase } from './FirebaseContext';
import { LocationOption, LocationHierarchyRole } from '../types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getStoreLocations } from '../services/storeLocationService';

interface LocationContextType {
  hierarchyRole: LocationHierarchyRole;
  setHierarchyRole: (role: LocationHierarchyRole) => void;
  activeLocationId: string;
  activeLocation: LocationOption;
  availableLocations: LocationOption[];
  isConsolidated: boolean;
  changeLocation: (locationId: string) => void;
  organizationName: string;
  assignedRegionName: string;
  loading: boolean;
  refreshLocations: () => Promise<void>;
}

// Standard multi-unit locations for demonstration and regional grouping
const DEMO_LOCATIONS: LocationOption[] = [
  { id: 'loc-downtown', name: 'Downtown Bistro', region: 'North Region', city: 'Seattle', state: 'WA' },
  { id: 'loc-uptown', name: 'Uptown Taphouse', region: 'North Region', city: 'Bellevue', state: 'WA' },
  { id: 'loc-airport', name: 'Airport Terminal 2', region: 'South Region', city: 'SeaTac', state: 'WA' },
  { id: 'loc-westside', name: 'Westside Cafe', region: 'West Region', city: 'Tacoma', state: 'WA' },
];

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, tenantId, role, userName } = useFirebase();
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@growezy.ai';

  // Determine base role from Firebase auth/user profile
  const initialRole: LocationHierarchyRole = useMemo(() => {
    if (isSuperAdmin || role === 'owner') return 'executive';
    if (role === 'manager' || role === 'admin') return 'manager';
    if (role === 'employee') return 'employee';
    return 'executive';
  }, [isSuperAdmin, role]);

  const [hierarchyRole, setHierarchyRoleState] = useState<LocationHierarchyRole>(() => {
    const saved = localStorage.getItem('tipvalue_hierarchy_role');
    return (saved as LocationHierarchyRole) || initialRole;
  });

  const [firestoreLocations, setFirestoreLocations] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const organizationName = "Apex Hospitality Group";
  const assignedRegionName = "North Region (2 Stores)";

  // Sync role if initial auth resolves
  useEffect(() => {
    if (!localStorage.getItem('tipvalue_hierarchy_role')) {
      setHierarchyRoleState(initialRole);
    }
  }, [initialRole]);

  // Load real Firestore locations and tenants
  const loadTenants = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);

      // First, try loading tenant-specific store locations
      if (tenantId) {
        const storeLocs = await getStoreLocations(tenantId);
        if (storeLocs.length > 0) {
          const mapped: LocationOption[] = storeLocs.map(s => ({
            id: s.id,
            name: s.name,
            code: s.code,
            address: s.address,
            city: s.city,
            state: s.state,
            zip: s.zip,
            region: s.region || 'North Region',
            timezone: s.timezone,
            phone: s.phone,
            status: s.status,
            isPrimary: s.isPrimary,
            staffCount: s.staffCount,
          }));
          setFirestoreLocations(mapped);
          setLoading(false);
          return;
        }
      }

      // Fallback: list all tenants
      const snapshot = await getDocs(collection(db, 'tenants'));
      const loaded: LocationOption[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.locationName || data.name || data.dbaName || `Store #${d.id.substring(0, 6)}`,
          dbaName: data.dbaName,
          city: data.city,
          state: data.state,
          region: 'North Region',
        };
      });
      if (loaded.length > 0) {
        setFirestoreLocations(loaded);
      }
    } catch (err) {
      console.warn('Could not list locations for location switcher, using standard locations', err);
    } finally {
      setLoading(false);
    }
  }, [user, tenantId]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Merge loaded locations with demo locations without duplicates
  const allLocations: LocationOption[] = useMemo(() => {
    const combined = [...firestoreLocations];
    for (const demo of DEMO_LOCATIONS) {
      if (!combined.some(l => l.name.toLowerCase() === demo.name.toLowerCase())) {
        combined.push(demo);
      }
    }
    return combined;
  }, [firestoreLocations]);

  // Scoped locations based on the active hierarchy role
  const availableLocations = useMemo((): LocationOption[] => {
    if (hierarchyRole === 'executive') {
      return [
        {
          id: 'all',
          name: 'All Locations (Group Total)',
          isConsolidatedOption: true,
        },
        ...allLocations,
      ];
    }

    if (hierarchyRole === 'regional_manager') {
      // Scoped only to assigned region (North Region)
      const northRegionStores = allLocations.filter(
        loc => !loc.region || loc.region === 'North Region'
      );
      return [
        {
          id: 'all',
          name: 'All My Assigned Stores (North Region Total)',
          isConsolidatedOption: true,
          region: 'North Region',
        },
        ...northRegionStores,
      ];
    }

    if (hierarchyRole === 'manager') {
      // Single store locked (Downtown Bistro or current tenant)
      const myStore = allLocations.find(l => l.id === tenantId) || allLocations[0] || DEMO_LOCATIONS[0];
      return [myStore];
    }

    // Employee
    return [];
  }, [hierarchyRole, allLocations, tenantId]);

  // Selected location state
  const [activeLocationId, setActiveLocationId] = useState<string>(() => {
    return localStorage.getItem('tipvalue_active_location') || 'all';
  });

  // Ensure activeLocationId is valid for current role
  useEffect(() => {
    if (hierarchyRole === 'manager') {
      const singleStore = availableLocations[0];
      if (singleStore && activeLocationId !== singleStore.id) {
        setActiveLocationId(singleStore.id);
      }
    } else if (hierarchyRole === 'employee') {
      // Employees are staff scoped
      setActiveLocationId('employee_personal');
    } else {
      const exists = availableLocations.some(l => l.id === activeLocationId);
      if (!exists && availableLocations.length > 0) {
        setActiveLocationId(availableLocations[0].id);
      }
    }
  }, [hierarchyRole, availableLocations, activeLocationId]);

  const changeLocation = (id: string) => {
    setActiveLocationId(id);
    localStorage.setItem('tipvalue_active_location', id);
  };

  const setHierarchyRole = (newRole: LocationHierarchyRole) => {
    setHierarchyRoleState(newRole);
    localStorage.setItem('tipvalue_hierarchy_role', newRole);
  };

  const activeLocation = useMemo((): LocationOption => {
    if (hierarchyRole === 'employee') {
      return {
        id: 'employee_personal',
        name: `${userName || 'Sarah Jenkins'} — Server Dashboard`,
      };
    }
    const match = availableLocations.find(l => l.id === activeLocationId);
    if (match) return match;
    return availableLocations[0] || DEMO_LOCATIONS[0];
  }, [availableLocations, activeLocationId, hierarchyRole, userName]);

  const isConsolidated = activeLocationId === 'all';

  return (
    <LocationContext.Provider
      value={{
        hierarchyRole,
        setHierarchyRole,
        activeLocationId,
        activeLocation,
        availableLocations,
        isConsolidated,
        changeLocation,
        organizationName,
        assignedRegionName,
        loading,
        refreshLocations: loadTenants,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useStoreLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    // Return safe fallback instead of hard-crashing the entire app
    return {
      hierarchyRole: 'executive' as LocationHierarchyRole,
      setHierarchyRole: () => {},
      activeLocationId: 'all',
      activeLocation: DEMO_LOCATIONS[0],
      availableLocations: DEMO_LOCATIONS,
      isConsolidated: true,
      changeLocation: () => {},
      organizationName: "Apex Hospitality Group",
      assignedRegionName: "North Region (2 Stores)",
      loading: false,
      refreshLocations: async () => {},
    };
  }
  return context;
};

// Backwards compatibility alias to avoid breaking any existing components
export const useLocation = useStoreLocation;
